<template>
    <div class="outfit-filter-container" :class="{ 'outfit-filter-container--no-clear-button': !showClearButtonClass }">
        <transition name="fade" @leave="clearHidden"  @enter="showClearButtonClass = showClearButton">
            <button class="clear-filter-button" @click="clearFilters" v-show="showClearButton">
                <span class="clear-filter-label">
                    Clear Filter
                    <span class="clear-cross"></span>
                </span>
            </button>
        </transition>
        <div class="filters-container">
            <transition-group name="fade">
                <div class="filter-container" v-if="sortOptions.length > 0" :key="sortFilterKey">
                    <full-view-select 
                        :options="sortOptions"
                        :optionSelectedIndex="sortOptionSelectedIndex"
                        defaultValueText="Sort By"
                        optionTextField="title"
                        @option-changed="handleSortChanged"
                    />
                </div>
                <div class="filter-container" :key="priceFilterKey">
                    <label class="filter-label">Price</label>
                    <div class="filter-options-container">
                        <div class="option option-price">
                            <div class="price-slider-container">
                                <input type="range" class="price-slider-input" v-model="priceSelected">
                                <div class="price-slider-input-bar price-slider-input-bar--filled" :style="{width: `${priceSelected}%`}"></div>
                                <div class="price-slider-input-bar price-slider-input-bar--unfilled" :style="{width: `${100 - priceSelected}%`, left: `${priceSelected}%`}"></div>
                            </div>
                        </div>
                        <span class="option-label option-label-min">Low</span>
                        <span class="option-label option-label-max">High</span>
                        
                    </div>
                </div>

                <div class="filter-container" v-if="seasonFilter.length > 0" :key="seasonFilterKey">
                    <label class="filter-label filter-label-season">Season</label>
                    <div class="filter-options-container">
                        <button
                            class="option option-season"
                            v-for="filter in seasonFilter"
                            :key="filter.product_option_value_id"
                            :class="{'option--active': optionsSelected[filter.product_option_value_id]}"
                            @click="changeFilterOption(filter.product_option_value_id)">
                            <season-icon :name="filter.product_option_value_title" />
                            <span class="option-label">{{filter.product_option_value_title}}</span>
                        </button>
                    </div>
                </div>
                    
                <div class="filter-container" v-if="occasionFilter.length > 0" :key="occasionFilterKey">
                    <label class="filter-label">Occasion Type</label>
                    <div class="filter-options-container filter-options-container-occasion">
                        <button 
                            v-for="filter in occasionFilter"
                            :key="filter.product_option_value_id"
                            class="option option-occasion"
                            :class="{'option--active': optionsSelected[filter.product_option_value_id]}"
                            @click="changeFilterOption(filter.product_option_value_id)">
                            {{filter.product_option_value_title}}
                        </button>
                    </div>
                </div>
            </transition-group>    
        </div>
        <button class="close-button" @click="close">Close</button>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import SeasonIcon from './season-icon.vue';
    import FullViewSelect from '../common/full-view-select.vue';
    import { SORT_FILTER, SEASON_FILTER, PRICE_FILTER, OCCASION_FILTER } from '../../config';

    export default {
        components: {
            SeasonIcon,
            FullViewSelect,
        },
        data() {
            return {
                seasonFilterKey: SEASON_FILTER,
                priceFilterKey: PRICE_FILTER,
                occasionFilterKey: OCCASION_FILTER,
                sortFilterKey: SORT_FILTER,
                showClearButtonClass: false,
                selectedFiltersChanged: false,
            };
        },
        computed: {
            priceSelected: {
                get() {
                    return this.pricePercentage || 50;
                },
                set(value) {
                    this.selectedFiltersChanged = true;
                    return this.$store.commit('filters/setSelectedFilter', {
                        pricePercentage: value, 
                    });

                    this.$helpers.trackEvent("set_price_filter", {});
                }
            },
            showClearButton() {
                return Object.values(this.optionsSelected).some(optionSelected => !!optionSelected) 
                    || this.sortOptionSelectedIndex != null
                    || this.pricePercentage != null;
            },
            ...mapGetters({
                seasonFilter: 'filters/getSeasonFilter',
                occasionFilter: 'filters/getOccasionFilter',
                optionsSelected: 'filters/getSelectedOptions',
                sortOptionSelectedIndex: 'filters/sortOptionIndexSelected',
                sortOptions: 'filters/getSortOptions',
                pricePercentage: 'filters/getPricePercentage',
            }),
        },
        methods: {
             changeFilterOption(optionValueId) {
                this.$store.commit('filters/setSelectedFilter', {
                    optionValues: {
                        ...this.optionsSelected,
                        [optionValueId]: !this.optionsSelected[optionValueId]
                    },
                });
                this.selectedFiltersChanged = true;

                this.$helpers.trackEvent("set_filter", {});
            },
            close() {
                this.$router.go(-1);
            },
            clearFilters() {
                this.selectedFiltersChanged = true;
                this.$store.commit('filters/resetSelectedFilters');

                this.$helpers.trackEvent("reset_filters", {});
            },
            clearHidden() {
                setTimeout(() => { this.showClearButtonClass = this.showClearButton }, 500);
            },
            handleSortChanged(index, option) {
                this.selectedFiltersChanged = true;
                this.$store.commit('filters/setSelectedSortIndex', index);
                this.$store.commit('filters/setSelectedFilter', {
                    sort: option, 
                });
            },
        },
        created() {
            if(this.seasonFilter.length === 0) {    
                this.$store.dispatch('filters/fetchFilterOption', {
                    filterType: SEASON_FILTER
                });
            }
            if(this.occasionFilter.length === 0) {
                this.$store.dispatch('filters/fetchFilterOption', {
                    filterType: OCCASION_FILTER
                });
            }
        },
        mounted() {
            this.showClearButtonClass = this.showClearButton;
        },
        beforeDestroy() {
            if(this.selectedFiltersChanged) this.$store.dispatch('filters/applyFilters');
        },
    }
</script>

<style lang="scss" scoped>
    .outfit-filter-container {
        display: grid;
        grid-template-rows: 60px 1fr 45px;
        min-height: calc(100vh - 62px);
        height: auto;
        padding: 10px 26px 0px;
        font-size:14px;
        @media screen and (min-height: 688px){
            grid-template-rows: 60px 1fr 70px;
            padding-top: 26px;
        }
        &--no-clear-button {
            grid-template-rows: 1fr 70px;
        }
        .clear-filter-button {
            background-color: #EFEFEF;
            height: 37px;
            width: 100%;
            border-radius: 19px;
            text-transform: uppercase;
            .clear-filter-label {
                margin-left: -23px;
                .clear-cross {
                    position: relative;
                    &:after, &:before {
                        content: '';
                        height: 14px;
                        border-left: 1px solid #000;
                        position: absolute;
                        left: 9px;
                    }
                    &:after {
                        transform: rotate(45deg);
                    }
                    &:before {
                        transform: rotate(-45deg);
                    }
                }
            }
        }
        .close-button {
            text-decoration: underline;
            text-transform: uppercase;
        }
        .filters-container {
            display: flex;
            flex-direction: column;
            
            .filter-container {
                margin-bottom: 15px;
                @media screen and (min-height: 688px){
                    margin-bottom: 35px;    
                }
                &:last-child {
                    margin-bottom: 0px;
                }
                .filter-label {
                    text-decoration: underline;
                    line-height: 16px;
                    font-size: 13px;
                    margin-bottom: 15px;
                    display: block;
                    &-season {
                        margin-bottom: 26px;
                    }
                }
                .filter-options-container {
                    display: flex;
                    flex-wrap: wrap;
                    justify-content: space-between;
                    &-occasion {
                        justify-content: flex-start;
                    }
                    .option {
                        &-season {
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                            opacity: 0.3;
                            .option-label {
                                font-size: 12px;
                                letter-spacing: 2px;
                                line-height: 14px;
                                text-transform: uppercase;
                                margin-top: 20px;
                            }
                            &.option--active {
                                opacity: 1;
                            }
                        }
                        &-occasion {
                            border: 1px solid #000000;
                            border-radius: 20px;
                            padding: 10px 15px;
                            font-size: 14px;
                            margin: 0px 8px 10px 0px;
                            &.option--active {
                                background-color: #000;
                               color: white;
                            }
                        }
                        &-price {
                            flex: 100%;
                            .price-slider-container {
                                position: relative;
                                .price-slider-input {
                                    width: 100%;
                                    -webkit-appearance: none;
                                    background: transparent;
                                    &-bar {
                                        height: 7px;
                                        border-radius: 20px;
                                        top: calc(50% - 3.5px);
                                        position: absolute;
                                        &--filled {
                                            background-color: #000;
                                        }
                                        &--unfilled {
                                            background-color: #EEEEEE;
                                        }
                                    }
                                    
                                    &::-webkit-slider-thumb {
                                        -webkit-appearance: none;
                                        height: 28px;
                                        width: 28px;
                                        border-radius: 50%;
                                        background-color: black;
                                        position: relative;
                                        z-index: 1;
                                        box-shadow: 0 1px 2px 0 rgba(0,0,0,0.22);
                                    }
                                }
                            }
                            
                            & ~ .option-label {
                                font-size: 10px;
                                &-max {
                                    margin-right: 5px;
                                }
                                &-min {
                                    margin-left: 5px;
                                }
                            }
                        }   
                    }
                }
            }   
        }
    }
</style>